
import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import {
  collection, query, where, orderBy, onSnapshot,
  addDoc, serverTimestamp, doc, updateDoc, deleteDoc,
  limit, getDoc, getDocs, writeBatch, Timestamp, collectionGroup, arrayUnion
} from 'firebase/firestore';
import { Card, Button, Input, Badge } from '../components/ui';
import { Connection, DirectMessage, UserProfile, Village } from '../types';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { useLocation } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet icons in Webpack/Vite bundles
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 3958.8; // miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

const DirectMessages: React.FC = () => {
  const { userProfile, currentUser } = useAuth();
  const location = useLocation();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [connectedUsers, setConnectedUsers] = useState<Record<string, UserProfile>>({});
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<DirectMessage[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [allPresence, setAllPresence] = useState<Record<string, boolean>>({});
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );

  const [villages, setVillages] = useState<Village[]>([]);
  const [selectedVillageId, setSelectedVillageId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'dms' | 'villages'>('dms');
  const [isCreateVillageOpen, setIsCreateVillageOpen] = useState(false);
  const [newVillageName, setNewVillageName] = useState('');
  const [newVillageDesc, setNewVillageDesc] = useState('');
  const [newVillageShield, setNewVillageShield] = useState('🛡️');
  const [selectedMembersList, setSelectedMembersList] = useState<string[]>([]);
  const [isCreatingVillage, setIsCreatingVillage] = useState(false);
  const [newVillageIsPublic, setNewVillageIsPublic] = useState(false);
  const [newVillageLocation, setNewVillageLocation] = useState('');
  const [publicVillages, setPublicVillages] = useState<Village[]>([]);
  const [searchHubQuery, setSearchHubQuery] = useState('');
  const [isSearchingNearby, setIsSearchingNearby] = useState(false);
  const [nearbyResults, setNearbyResults] = useState<{hub: Village, distance: number}[] | null>(null);

  const [discoverSuggestions, setDiscoverSuggestions] = useState<any[]>([]);
  const [isSearchingDiscover, setIsSearchingDiscover] = useState(false);
  const [discoverCoordinates, setDiscoverCoordinates] = useState<[number, number] | null>(null);

  const [locationSuggestions, setLocationSuggestions] = useState<any[]>([]);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [selectedCoordinates, setSelectedCoordinates] = useState<[number, number] | null>(null);

  const [mentions, setMentions] = useState<any[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    if (location.search.includes('createHub=true')) {
        setIsCreateVillageOpen(true);
        setActiveTab('villages');
    }

    return () => { isMounted.current = false; };
  }, [location.search]);

  const requestNotificationPermission = async () => {
    if (typeof Notification === 'undefined') return;
    const permission = await Notification.requestPermission();
    setNotificationPermission(permission);

    if (permission === 'granted' && currentUser) {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        notificationsEnabled: true,
        notificationSettingsUpdatedAt: serverTimestamp()
      });
    }
  };

  // Global Presence Sync
  useEffect(() => {
    const q = query(collection(db, 'presence'));
    const unsub = onSnapshot(q, (snap) => {
      const active: Record<string, boolean> = {};
      const now = Date.now();
      snap.forEach(d => {
        const data = d.data();
        // Ignore stale presence older than 2.5 minutes (150,000ms), assume Date.now() if pending writes (null)
        const lastActiveMs = data.lastActive?.toMillis ? data.lastActive.toMillis() : now;
        if (now - lastActiveMs <= 150000) {
          active[d.id] = true;
        }
      });
      setAllPresence(active);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!currentUser || connections.length === 0) return;

    // Fetch Mentions
    if (userProfile?.nickname) {
      const qMentions = query(
        collection(db, 'notifications'),
        where('recipientName', '==', userProfile.nickname),
        where('type', '==', 'mention'),
        where('read', '==', false)
      );
      const unsubMentions = onSnapshot(qMentions, (snap) => {
        if (!isMounted.current) return;
        setMentions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      });
      // Will auto unsubscribe implicitly or we can attach to the same cleanup logic if needed.
      // But typically we return it, here we don't have a specific compound cleanup, so we can use a separate effect.
    }
  }, [currentUser, userProfile?.nickname, connections.length]);

  useEffect(() => {
    if (!currentUser || connections.length === 0) return;

    const performPurge = async () => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const cutoffTimestamp = Timestamp.fromDate(thirtyDaysAgo);

      for (const conn of connections) {
        if (conn.status === 'accepted') {
          try {
            const oldMessagesQuery = query(
              collection(db, `connections/${conn.id}/messages`),
              where('createdAt', '<', cutoffTimestamp),
              limit(50)
            );

            const snapshot = await getDocs(oldMessagesQuery);
            if (!snapshot.empty) {
              const batch = writeBatch(db);
              snapshot.docs.forEach((doc) => batch.delete(doc.ref));
              await batch.commit();
            }
          } catch (err) {
            console.error("Auto-purge failed:", conn.id, err);
          }
        }
      }
    };

    performPurge();
  }, [currentUser?.uid, connections]);

  useEffect(() => {
    if (!currentUser) return;

    const q = query(
      collection(db, 'connections'),
      where('users', 'array-contains', currentUser.uid)
    );

    const unsub = onSnapshot(q, (snap) => {
      if (!isMounted.current) return;
      const connData = snap.docs.map(d => ({ id: d.id, ...d.data() } as Connection));
      setConnections(connData);
    }, (err) => {
      console.error("Connection sync error:", err);
    });

    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const q = query(collection(db, 'villages'), where('members', 'array-contains', currentUser.uid));
    const unsub = onSnapshot(q, snap => {
      if (!isMounted.current) return;
      setVillages(snap.docs.map(d => ({ id: d.id, ...d.data() } as Village)));
    }, err => console.error("Village sync error:", err));
    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const qPub = query(collection(db, 'villages'), where('isPublic', '==', true), limit(50));
    const unsubPub = onSnapshot(qPub, snap => {
       if (!isMounted.current) return;
       const docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as Village));
       setPublicVillages(docs);
    }, err => console.error("Public Hubs sync error:", err));
    return () => unsubPub();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || (connections.length === 0 && villages.length === 0)) return;

    const fetchProfiles = async () => {
      if (!isMounted.current) return;
      setIsLoadingProfiles(true);
      
      const allUids = [
          ...connections.flatMap(c => c.users), 
          ...villages.flatMap(v => v.members)
      ].filter(uid => uid !== currentUser.uid);

      const userUids = Array.from(new Set(allUids)) as string[];

      const profiles: Record<string, UserProfile> = { ...connectedUsers };
      let changed = false;

      for (const uid of userUids) {
        if (!profiles[uid]) {
          try {
            const uSnap = await getDoc(doc(db, 'users', uid));
            if (uSnap.exists()) {
              profiles[uid] = uSnap.data() as UserProfile;
              changed = true;
            }
          } catch (err) {
            console.error(`Error fetching profile for ${uid}:`, err);
          }
        }
      }

      if (changed && isMounted.current) setConnectedUsers(profiles);
      if (isMounted.current) setIsLoadingProfiles(false);
    };

    fetchProfiles();
  }, [connections, villages, currentUser?.uid]);

  // Optimized Unread Counting: Filters by receiverId to satisfy security rules
  useEffect(() => {
    if (!currentUser || connections.length === 0) return;

    const q = query(
      collectionGroup(db, 'messages'),
      where('receiverId', '==', currentUser.uid),
      where('read', '==', false)
    );

    const unsubGlobal = onSnapshot(q, (snap) => {
      if (!isMounted.current) return;
      const counts: Record<string, number> = {};

      snap.docs.forEach(d => {
        const pathSegments = d.ref.path.split('/');
        const connId = pathSegments[1];
        counts[connId] = (counts[connId] || 0) + 1;
      });

      setUnreadCounts(counts);
    }, (err) => {
      console.error("Unread count snapshot error:", err);
    });

    return () => unsubGlobal();
  }, [connections.length, currentUser?.uid]);

  useEffect(() => {
    if (!selectedConnectionId || !currentUser) return;

    const markAsRead = async () => {
      const q = query(
        collection(db, `connections/${selectedConnectionId}/messages`),
        where('receiverId', '==', currentUser.uid),
        where('read', '==', false)
      );

      try {
        const snap = await getDocs(q);
        if (!isMounted.current) return;

        if (snap.size > 0) {
          const batch = writeBatch(db);
          snap.docs.forEach(d => {
            batch.update(d.ref, { read: true });
          });
          await batch.commit();

          if (isMounted.current) {
            setUnreadCounts(prev => ({
              ...prev,
              [selectedConnectionId]: 0
            }));
          }
        }
      } catch (err) {
        console.error("Failed to mark messages as read:", err);
      }
    };

    markAsRead();
  }, [selectedConnectionId, messages.length, currentUser?.uid]);

  useEffect(() => {
    if (!selectedConnectionId && !selectedVillageId) {
      setMessages([]);
      return;
    }
    const path = selectedVillageId ? `villages/${selectedVillageId}/messages` : `connections/${selectedConnectionId}/messages`;
    const q = query(collection(db, path), orderBy('createdAt', 'asc'), limit(100));

    const unsub = onSnapshot(q, (snap) => {
      if (!isMounted.current) return;
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() } as DirectMessage)));
    }, (err) => console.error("Message sync error:", err));
    return () => unsub();
  }, [selectedConnectionId, selectedVillageId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || (!selectedConnectionId && !selectedVillageId) || !userProfile || isSending) return;
    setIsSending(true);

    try {
      const isVillage = !!selectedVillageId;
      const path = isVillage ? `villages/${selectedVillageId}/messages` : `connections/${selectedConnectionId}/messages`;
      
      let receiverUid = null;
      if (!isVillage) {
        const conn = connections.find(c => c.id === selectedConnectionId);
        if (conn) receiverUid = conn.users.find(u => u !== currentUser?.uid) || null;
      }

      await addDoc(collection(db, path), {
        senderId: userProfile.uid,
        receiverId: receiverUid,
        senderName: userProfile.nickname || userProfile.displayName,
        text: newMessage,
        createdAt: serverTimestamp(),
        read: false
      });
      
      if (isVillage) {
         await updateDoc(doc(db, 'villages', selectedVillageId!), {
            updatedAt: serverTimestamp(),
            lastMessage: { text: newMessage.substring(0, 50), senderName: userProfile.nickname || userProfile.displayName, createdAt: serverTimestamp() }
         });
      } else {
         await updateDoc(doc(db, 'connections', selectedConnectionId!), { updatedAt: serverTimestamp() });
      }
      if (isMounted.current) setNewMessage('');
    } catch (err) {
      console.error("Failed to send message:", err);
    } finally {
      if (isMounted.current) setIsSending(false);
    }
  };

  const handleCreateVillage = async () => {
      if (!newVillageName.trim() || !currentUser || !userProfile) return;
      setIsCreatingVillage(true);
      try {
          const members = [currentUser.uid, ...selectedMembersList];
          let coordinates: [number, number] | undefined = selectedCoordinates || undefined;

          if (!coordinates && newVillageIsPublic && newVillageLocation) {
              try {
                  const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(newVillageLocation)}`);
                  const data = await res.json();
                  if (data && data.length > 0) {
                      coordinates = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
                  }
              } catch (e) {
                  console.error("Geocoding failed", e);
              }
          }

          await addDoc(collection(db, 'villages'), {
              name: newVillageName,
              description: newVillageDesc,
              shieldIcon: newVillageShield,
              createdBy: currentUser.uid,
              members: members,
              isPublic: newVillageIsPublic,
              location: newVillageLocation,
              ...(coordinates ? { coordinates } : {}),
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
          });
          setIsCreateVillageOpen(false); setNewVillageName(''); setNewVillageDesc(''); setSelectedMembersList([]); setNewVillageLocation(''); setNewVillageIsPublic(false); setActiveTab('villages'); setSelectedCoordinates(null);
      } catch (err) { console.error(err); } finally { setIsCreatingVillage(false); }
  };

  const handleJoinVillage = async (villageId: string) => {
      if (!currentUser) return;
      try {
          const villageRef = doc(db, 'villages', villageId);
          const snap = await getDoc(villageRef);
          if (snap.exists()) {
              const members = snap.data().members || [];
              if (!members.includes(currentUser.uid)) {
                  await updateDoc(villageRef, { members: [...members, currentUser.uid] });
              }
              setSelectedVillageId(villageId);
          }
      } catch (err) {
          console.error("Failed to join public hub:", err);
      }
  };

  const handleLeaveVillage = async () => {
     if (!selectedVillage || !currentUser) return;
     if (!window.confirm("Are you sure you want to leave this hub? You will lose access to the chat. If you are the last member, the hub will be permanently deleted.")) return;

     const newMembers = selectedVillage.members.filter(uid => uid !== currentUser.uid);
     
     if (newMembers.length === 0) {
         try {
             await deleteDoc(doc(db, 'villages', selectedVillage.id!));
             setSelectedVillageId(null);
             setActiveTab('villages');
         } catch (err) {
             console.error("Failed to delete empty hub:", err);
         }
     } else {
         try {
             await updateDoc(doc(db, 'villages', selectedVillage.id!), { members: newMembers });
             setSelectedVillageId(null);
             setActiveTab('villages');
         } catch (err) {
             console.error("Failed to leave hub:", err);
         }
     }
  };

  const handleAcceptConnection = async (id: string) => {
    try {
      await updateDoc(doc(db, 'connections', id), {
        status: 'accepted',
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error("Accept failed:", err);
    }
  };

  const markMentionRead = async (mentionId: string) => {
    try {
      await updateDoc(doc(db, 'notifications', mentionId), { read: true });
    } catch (err) {}
  };

  const handleUnlink = async (id: string) => {
    if (!window.confirm("Disconnect from this user? This will end the peer support relationship and permanently hide chat history.")) return;
    try {
      await deleteDoc(doc(db, 'connections', id));
      if (isMounted.current) setSelectedConnectionId(null);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleBlockUser = async (id: string, otherUserId: string) => {
    if (!window.confirm("Block this user? They will no longer be able to contact you.")) return;
    try {
      await deleteDoc(doc(db, 'connections', id));
      
      if (currentUser) {
          const userRef = doc(db, 'users', currentUser.uid);
          await updateDoc(userRef, {
              blockedUsers: arrayUnion(otherUserId)
          });
      }
      
      if (isMounted.current) setSelectedConnectionId(null);
      alert("User blocked successfully.");
    } catch (err) {
      console.error("Block failed:", err);
    }
  };

  const pendingRequests = connections.filter(c => c.status === 'pending' && c.receiverUid === currentUser?.uid);
  const activeChats = connections.filter(c => c.status === 'accepted');

  const selectedConn = connections.find(c => c.id === selectedConnectionId);
  const otherParticipantId = selectedConn?.users.find(uid => uid !== currentUser?.uid);
  const otherParticipant = otherParticipantId ? connectedUsers[otherParticipantId] : null;

  const selectedVillage = villages.find(v => v.id === selectedVillageId);

  useEffect(() => {
    if (!newVillageLocation || selectedCoordinates || !newVillageIsPublic) {
        setLocationSuggestions([]);
        return;
    }
    const delay = setTimeout(async () => {
        setIsSearchingLocation(true);
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&countrycodes=gb&q=${encodeURIComponent(newVillageLocation)}`);
            const data = await res.json();
            setLocationSuggestions(data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setIsSearchingLocation(false);
        }
    }, 800);
    return () => clearTimeout(delay);
  }, [newVillageLocation, selectedCoordinates, newVillageIsPublic]);

  useEffect(() => {
    if (!searchHubQuery.trim() || discoverCoordinates) {
        setDiscoverSuggestions([]);
        return;
    }
    const delay = setTimeout(async () => {
        setIsSearchingDiscover(true);
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&countrycodes=gb&q=${encodeURIComponent(searchHubQuery)}`);
            const data = await res.json();
            setDiscoverSuggestions(data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setIsSearchingDiscover(false);
        }
    }, 400);
    return () => clearTimeout(delay);
  }, [searchHubQuery, discoverCoordinates]);

  useEffect(() => {
     if (!searchHubQuery.trim()) { setNearbyResults(null); return; }
     
     if (discoverCoordinates) {
         const [sLat, sLon] = discoverCoordinates;
         const withDist = publicVillages.map(v => {
             if (!v.coordinates) return { hub: v, distance: 999999 };
             return { hub: v, distance: calculateDistance(sLat, sLon, v.coordinates[0], v.coordinates[1]) };
         }).sort((a,b) => a.distance - b.distance);
         setNearbyResults(withDist.slice(0, 3));
         setIsSearchingNearby(false);
         return;
     }

     const delay = setTimeout(async () => {
         const textMatch = publicVillages.filter(v => 
             v.name.toLowerCase().includes(searchHubQuery.toLowerCase()) || 
             v.location.toLowerCase().includes(searchHubQuery.toLowerCase())
         );
         if (textMatch.length === 0 && searchHubQuery.length > 2) {
             setIsSearchingNearby(true);
             try {
                 const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&countrycodes=gb&q=${encodeURIComponent(searchHubQuery)}`);
                 const data = await res.json();
                 if (data && data.length > 0) {
                     const sLat = parseFloat(data[0].lat);
                     const sLon = parseFloat(data[0].lon);
                     const withDist = publicVillages.map(v => {
                         if (!v.coordinates) return { hub: v, distance: 999999 };
                         return { hub: v, distance: calculateDistance(sLat, sLon, v.coordinates[0], v.coordinates[1]) };
                     }).sort((a,b) => a.distance - b.distance);
                     setNearbyResults(withDist.slice(0, 3));
                 } else {
                     setNearbyResults([]);
                 }
             } catch (e) {
                 setNearbyResults([]);
             } finally {
                 setIsSearchingNearby(false);
             }
         } else {
             setNearbyResults(null);
         }
     }, 1000);
     return () => clearTimeout(delay);
  }, [searchHubQuery, publicVillages]);

  const textMatchHubs = searchHubQuery 
      ? publicVillages.filter(v => v.name.toLowerCase().includes(searchHubQuery.toLowerCase()) || v.location.toLowerCase().includes(searchHubQuery.toLowerCase()))
      : publicVillages;
  const displayedHubs = nearbyResults ? nearbyResults.map(r => r.hub) : textMatchHubs;

  const joinableHubsList = nearbyResults 
      ? nearbyResults.filter(r => currentUser && !r.hub.members.includes(currentUser.uid))
      : textMatchHubs.filter(v => currentUser && !v.members.includes(currentUser.uid)).map(hub => ({ hub, distance: null }));

  return (
    <Layout fluid>
      <div className="flex h-full bg-[#f8fafc] overflow-hidden">

        {/* Sidebar: Connections List */}
        <aside className={`w-full md:w-80 flex-shrink-0 bg-white border-r border-slate-200 flex flex-col h-full overflow-hidden z-20 ${selectedConnectionId ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-6 border-b border-slate-200 bg-white flex justify-between items-center">
            <div>
              <h2 className="text-xl font-heading font-bold text-slate-900">Direct Messages</h2>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Private Peer Support</p>
            </div>
            {notificationPermission !== 'granted' && (
              <button
                onClick={requestNotificationPermission}
                className="w-8 h-8 rounded-full bg-amber-50 text-amber-500 flex items-center justify-center hover:bg-amber-100 transition-colors"
                title="Enable Push Notifications"
              >
                🔔
              </button>
            )}
          </div>

          <div className="flex px-6 pt-0 pb-0 gap-4 border-b border-slate-200 bg-white shadow-sm relative z-10 flex-shrink-0">
             <button onClick={() => setActiveTab('dms')} className={`pb-3 pt-4 text-[10px] font-black uppercase tracking-widest border-b-2 flex-1 text-center transition-all ${activeTab === 'dms' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>Private Chats</button>
             <button onClick={() => setActiveTab('villages')} className={`pb-3 pt-4 text-[10px] font-black uppercase tracking-widest border-b-2 flex-1 text-center transition-all ${activeTab === 'villages' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>Local Hubs {villages.length > 0 ? `(${villages.length})` : ''}</button>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar">

            {activeTab === 'villages' && (
              <>
                 <div className="p-4 border-b border-slate-100 bg-slate-50/50">
                   <button onClick={() => setIsCreateVillageOpen(true)} className="w-full py-3 bg-white border border-dashed border-indigo-300 text-indigo-600 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-indigo-50 hover:border-indigo-500 transition-all flex items-center justify-center gap-2 shadow-sm">
                     <span className="text-sm">➕</span> Form a Local Hub
                   </button>
                 </div>
                 <div className="divide-y divide-slate-100">
                    {villages.length === 0 ? (
                      <div className="py-16 text-center px-8">
                        <span className="text-4xl block mb-4 grayscale opacity-40">⛺</span>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">No Local Hubs</p>
                      </div>
                    ) : (
                      villages.map(v => {
                         const isActive = selectedVillageId === v.id;
                         return (
                         <button key={v.id} onClick={() => { setSelectedVillageId(v.id!); setSelectedConnectionId(null); }} className={`w-full p-4 flex items-center gap-4 transition-all text-left group ${isActive ? 'bg-slate-50 border-r-4 border-indigo-500' : 'hover:bg-slate-50'}`}>
                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-2xl transition-all shadow-sm border border-slate-100 ${isActive ? 'bg-indigo-100' : 'bg-white group-hover:bg-indigo-50'}`}>
                               {v.shieldIcon || '🛡️'}
                            </div>
                            <div className="min-w-0 flex-1">
                               <p className={`text-sm font-bold truncate ${isActive ? 'text-indigo-900' : 'text-slate-700'}`}>{v.name}</p>
                               <p className="text-[10px] text-slate-400 truncate mt-0.5">{v.members.length} members {v.lastMessage ? ` • ${v.lastMessage.senderName}: ${v.lastMessage.text}` : ''}</p>
                            </div>
                         </button>
                         );
                      })
                    )}
                 </div>

                 {publicVillages.length > 0 && (
                     <div className="mt-4 border-t border-slate-100 bg-slate-50/50 pb-4">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-slate-50/90 backdrop-blur z-10">
                           <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Discover Public Hubs</p>
                           <Badge color="blue">{publicVillages.length}</Badge>
                        </div>

                        <div className="p-4 border-b border-slate-100 bg-white relative">
                           <Input 
                              label=""
                              placeholder="Search by name or location..." 
                              value={searchHubQuery} 
                              onChange={(e) => {
                                  setSearchHubQuery(e.target.value);
                                  setDiscoverCoordinates(null);
                              }} 
                              className="mb-0 text-sm" 
                           />
                           {isSearchingDiscover && <div className="absolute right-7 top-7 text-[10px] text-slate-400 font-bold animate-pulse">Searching...</div>}
                           {discoverSuggestions.length > 0 && !discoverCoordinates && (
                               <div className="absolute z-50 left-4 right-4 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-48 overflow-y-auto custom-scrollbar">
                                   {discoverSuggestions.map((loc, i) => (
                                       <button 
                                           key={i} 
                                           type="button"
                                           onClick={() => {
                                               setSearchHubQuery(loc.display_name);
                                               setDiscoverCoordinates([parseFloat(loc.lat), parseFloat(loc.lon)]);
                                               setDiscoverSuggestions([]);
                                           }}
                                           className="w-full text-left px-4 py-3 text-[11px] font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 border-b border-slate-100 last:border-0"
                                       >
                                           {loc.display_name}
                                       </button>
                                   ))}
                               </div>
                           )}
                        </div>

                        {displayedHubs.some(v => v.coordinates) && (
                            <div className="h-48 w-full z-0 relative border-b border-slate-200 shadow-inner overflow-hidden">
                               <MapContainer key={displayedHubs[0]?.id || 'default'} center={displayedHubs[0]?.coordinates || [54.5, -2.5]} zoom={nearbyResults ? 10 : (displayedHubs.length === 1 ? 10 : 5)} scrollWheelZoom={false} style={{ height: '100%', width: '100%', zIndex: 0 }}>
                                   <TileLayer
                                        attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                   />
                                   {displayedHubs.map(v => v.coordinates && (
                                       <Marker key={'map-'+v.id} position={v.coordinates as [number, number]}>
                                            <Popup>
                                                <b className="font-heading">{v.name}</b><br/>
                                                <span className="text-xs text-slate-500">{v.location}</span>
                                            </Popup>
                                       </Marker>
                                   ))}
                               </MapContainer>
                            </div>
                        )}

                        {(isSearchingNearby || nearbyResults) && (
                           <div className="p-3 px-6 bg-slate-50 border-b border-slate-100 text-center">
                              {isSearchingNearby && <p className="text-[10px] font-bold text-amber-600 animate-pulse">Searching nearby areas...</p>}
                              {nearbyResults && !isSearchingNearby && <p className="text-[10px] font-bold text-indigo-600">No exact match. Showing closest hubs.</p>}
                           </div>
                        )}

                        <div className="divide-y divide-slate-100 flex flex-col pt-1 pb-16">
                           {joinableHubsList.map(({ hub: v, distance }) => (
                               <div key={v.id} className="bg-white p-4 flex items-center justify-between gap-3 shadow-sm hover:border-indigo-300 transition-all border-l-4 border-l-transparent hover:border-l-indigo-400 group flex-shrink-0">
                                   <div className="flex items-center gap-3 min-w-0">
                                       <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-xl shadow-sm border border-slate-100 flex-shrink-0 group-hover:scale-110 transition-transform">{v.shieldIcon || '🛡️'}</div>
                                       <div className="min-w-0 w-full overflow-hidden">
                                           <p className="text-sm font-black text-slate-900 truncate leading-none mb-1">{v.name}</p>
                                           <p className="text-[10px] text-slate-600 truncate italic mb-1.5">{v.description}</p>
                                           <div className="flex flex-wrap items-center gap-1">
                                               <span className="text-[8px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-bold uppercase tracking-widest truncate max-w-[120px]">
                                                   📍 {v.location.split(',').slice(0, 1).join(',')}
                                               </span>
                                               {distance !== null && (
                                                   <span className="text-[8px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-bold uppercase tracking-widest whitespace-nowrap shadow-sm">
                                                       {distance.toFixed(1)} miles
                                                   </span>
                                               )}
                                               <span className="text-[8px] text-slate-500 font-bold uppercase tracking-widest">
                                                   • {v.members.length}
                                               </span>
                                           </div>
                                       </div>
                                   </div>
                                   <button onClick={() => handleJoinVillage(v.id!)} className="px-3 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-indigo-600 hover:text-white transition-all whitespace-nowrap shadow-sm">Join</button>
                               </div>
                           ))}
                           {joinableHubsList.length === 0 && !isSearchingNearby && (
                               <p className="p-4 text-xs text-slate-400 italic text-center">No hubs available to join.</p>
                           )}
                           
                           <div className="mt-2 pt-4 w-full flex justify-center pb-2">
                                <button onClick={() => setIsCreateVillageOpen(true)} className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-bold uppercase tracking-widest rounded-lg hover:bg-slate-100 hover:text-slate-900 transition-colors shadow-sm">
                                    <span>+</span> Form a Local Hub Instead
                                </button>
                           </div>
                        </div>
                     </div>
                 )}
              </>
            )}

            {activeTab === 'dms' && (
              <>
            {mentions.length > 0 && (
              <div className="p-4 bg-indigo-50/20 border-b border-indigo-100/50">
                <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                  <span className="text-sm bg-indigo-100 text-indigo-700 w-5 h-5 rounded-full flex items-center justify-center -mt-0.5">@</span> New Mentions ({mentions.length})
                </p>
                <div className="space-y-2">
                  {mentions.map(m => (
                    <div key={m.id} className="bg-white p-3 rounded-xl border border-indigo-100 shadow-sm flex flex-col gap-2">
                      <div className="flex items-center justify-between min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-md bg-indigo-600 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                            {m.senderName?.charAt(0).toUpperCase()}
                          </div>
                          <p className="text-[11px] font-bold text-slate-800 truncate">@{m.senderName} <span className="font-normal text-slate-500">mentioned you</span></p>
                        </div>
                        <button onClick={() => markMentionRead(m.id)} className="w-6 h-6 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-colors">✕</button>
                      </div>
                      <p className="text-[11px] text-slate-600 line-clamp-2 italic border-l-2 border-indigo-200 pl-2 ml-1 leading-snug">"{m.text}"</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {pendingRequests.length > 0 && (
              <div className="p-4 bg-indigo-50/50 border-b border-indigo-100">
                <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-3">Incoming Requests ({pendingRequests.length})</p>
                <div className="space-y-2">
                  {pendingRequests.map(conn => {
                    const profile = connectedUsers[conn.requesterUid];
                    return (
                      <div key={conn.id} className="bg-white p-3 rounded-xl border border-indigo-100 shadow-sm flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {profile?.nickname?.charAt(0).toUpperCase() || '?'}
                          </div>
                          <p className="text-xs font-bold text-slate-900 truncate">@{profile?.nickname || 'User'}</p>
                        </div>
                        <button
                          onClick={() => handleAcceptConnection(conn.id)}
                          className="bg-indigo-600 text-white text-[9px] font-black uppercase px-3 py-1.5 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
                        >
                          Accept
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="divide-y divide-slate-100">
              {activeChats.length === 0 ? (
                <div className="py-20 text-center px-8">
                  <span className="text-4xl block mb-4 grayscale opacity-40">✉️</span>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">No Active Chats</p>
                </div>
              ) : (
                activeChats.map(conn => {
                  const partnerId = conn.users.find(uid => uid !== currentUser?.uid);
                  const partner = partnerId ? connectedUsers[partnerId] : null;
                  const isActive = selectedConnectionId === conn.id;
                  const unreadCount = unreadCounts[conn.id] || 0;
                  const isOnline = partnerId ? allPresence[partnerId] : false;

                  return (
                    <button
                      key={conn.id}
                      onClick={() => { setSelectedConnectionId(conn.id); setSelectedVillageId(null); }}
                      className={`w-full p-4 flex items-center gap-4 transition-all text-left group ${isActive ? 'bg-slate-50 border-r-4 border-indigo-500' : 'hover:bg-slate-50'}`}
                    >
                      <div className="relative">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-lg font-bold transition-all ${isActive ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-100 text-slate-500 group-hover:bg-indigo-100 group-hover:text-indigo-600'}`}>
                          {partner?.nickname?.charAt(0).toUpperCase() || '?'}
                        </div>
                        {isOnline && (
                          <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-white shadow-md animate-pulse"></div>
                        )}
                        {unreadCount > 0 && !isActive && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-indigo-600 text-white text-[10px] font-black rounded-full flex items-center justify-center border-2 border-white animate-bounce shadow-sm">
                            {unreadCount}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex justify-between items-start">
                          <p className={`text-sm font-bold truncate ${isActive ? 'text-indigo-900' : 'text-slate-700'}`}>@{partner?.nickname || 'Member'}</p>
                        </div>
                        <p className={`text-[10px] truncate mt-0.5 ${unreadCount > 0 ? 'text-indigo-600 font-bold' : 'text-slate-400'}`}>
                          {unreadCount > 0 ? 'New Message' : isOnline ? 'Online Now' : 'Secure Connection'}
                        </p>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
            </>
            )}
          </div>
        </aside>

        {/* Chat Window Area */}
        <main className={`flex-1 flex flex-col bg-white overflow-hidden relative ${(!selectedConnectionId && !selectedVillageId) ? 'hidden md:flex' : 'flex'}`}>
          {(selectedConnectionId && otherParticipant) || selectedVillage ? (
            <>
              {/* Header */}
              <div className="p-4 md:px-8 md:py-5 border-b border-slate-200 flex items-center justify-between bg-white z-20 flex-shrink-0">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => { setSelectedConnectionId(null); setSelectedVillageId(null); }}
                    className="md:hidden text-slate-400 p-2 mr-2 bg-slate-50 rounded-xl hover:text-indigo-600 hover:bg-indigo-50 transition-all"
                  >
                    <span className="text-xl leading-none">←</span>
                  </button>
                  
                  {selectedVillage ? (
                     <>
                        <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center text-xl shadow-md border border-indigo-500">
                           {selectedVillage.shieldIcon || '🛡️'}
                        </div>
                        <div>
                           <h3 className="font-bold text-slate-900 text-sm">{selectedVillage.name}</h3>
                           <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{selectedVillage.members.length} Dads in Hub</p>
                        </div>
                     </>
                  ) : (
                     <div className="flex items-center gap-4">
                        <div className="relative">
                          <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center text-sm font-bold shadow-md">
                            {otherParticipant?.nickname?.charAt(0).toUpperCase()}
                          </div>
                          {otherParticipantId && allPresence[otherParticipantId] && (
                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white animate-pulse"></div>
                          )}
                        </div>
                        <div>
                          <h3 className="font-bold text-slate-900 text-sm">@{otherParticipant?.nickname}</h3>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${otherParticipantId && allPresence[otherParticipantId] ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-slate-300'}`}></span>
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{otherParticipantId && allPresence[otherParticipantId] ? 'Online Now' : 'Offline'}</span>
                          </div>
                        </div>
                     </div>
                  )}
                </div>
                
                {selectedVillage ? (
                  <button
                    onClick={handleLeaveVillage}
                    className="text-[9px] font-black uppercase tracking-widest transition-colors p-2 text-rose-500 hover:bg-rose-50 rounded-lg"
                  >
                    Leave Hub
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleUnlink(selectedConnectionId!)}
                        className="text-[9px] font-black text-slate-300 uppercase tracking-widest hover:text-rose-500 transition-colors p-2"
                      >
                        Disconnect
                      </button>
                      <button
                        onClick={() => handleBlockUser(selectedConnectionId!, otherParticipantId!)}
                        className="text-[9px] font-black text-white bg-rose-500 uppercase tracking-widest hover:bg-rose-600 transition-colors px-3 py-1.5 rounded-lg shadow-sm"
                      >
                        Block User
                      </button>
                  </div>
                )}
              </div>

              {selectedVillage && selectedVillage.pinnedEvent && (
                 <div className="bg-amber-50 border-b border-amber-100 p-3 px-4 md:px-8 flex items-start gap-3 shadow-inner">
                    <div className="text-xl">📅</div>
                    <div>
                        <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest leading-none mb-1">Pinned Hub Event</p>
                        <p className="text-sm font-bold text-slate-800">{selectedVillage.pinnedEvent.title} <span className="font-normal text-slate-500">• {selectedVillage.pinnedEvent.date}</span></p>
                        <p className="text-xs text-slate-600 mt-0.5">{selectedVillage.pinnedEvent.details}</p>
                    </div>
                 </div>
              )}

              {/* Messages */}
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto bg-white custom-scrollbar scroll-smooth"
              >
                <div className="max-w-3xl mx-auto w-full px-4 md:px-6 py-10 space-y-1">
                  <div className="flex flex-col items-center justify-center pb-12 opacity-50">
                    <div className="w-12 h-12 rounded-full border border-slate-100 flex items-center justify-center text-xl mb-3 shadow-sm bg-slate-50">{selectedVillage ? selectedVillage.shieldIcon || '🛡️' : '🛡️'}</div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 text-center">History started with {selectedVillage ? selectedVillage.name : `@${otherParticipant?.nickname}`}</p>
                    <p className="text-[9px] text-slate-300 max-w-[250px] text-center mt-2 italic leading-relaxed">Messages are secure.</p>
                  </div>

                  {messages.map((msg, idx) => {
                    const isMine = msg.senderId === currentUser?.uid;
                    const showHeader = idx === 0 || messages[idx - 1].senderId !== msg.senderId;

                    return (
                      <div key={msg.id} className={`flex gap-3 md:gap-4 group transition-all ${showHeader ? 'pt-6' : 'pt-1'}`}>
                        <div className="w-8 md:w-10 flex-shrink-0 flex justify-center">
                          {showHeader ? (
                            <div className={`w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center text-[10px] md:text-sm font-bold shadow-sm ${isMine ? 'bg-indigo-50 text-indigo-600 border border-indigo-100' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>
                              {isMine ? 'Me' : (otherParticipant?.nickname?.charAt(0).toUpperCase() || '?')}
                            </div>
                          ) : (
                            <div className="w-8 md:w-10 h-full flex flex-col items-center">
                              <div className="w-px h-full bg-slate-50"></div>
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          {showHeader && (
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-[10px] md:text-[11px] font-black uppercase tracking-wider ${isMine ? 'text-indigo-600' : 'text-slate-800'}`}>
                                {isMine ? 'You' : `@${otherParticipant?.nickname || 'Unknown'}`}
                              </span>
                              <span className="text-[9px] text-slate-400 font-bold">
                                {msg.createdAt ? new Date(msg.createdAt.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '...'}
                              </span>
                              {isMine && msg.read && (
                                <span className="text-[8px] font-black uppercase text-indigo-500 flex items-center gap-0.5 bg-indigo-50 px-1.5 py-0.5 rounded ml-1 animate-fade-in">
                                  <span className="text-xs">✓✓</span> READ
                                </span>
                              )}
                            </div>
                          )}
                          <div className={`p-3 md:p-4 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap border shadow-sm transition-shadow group-hover:shadow-md ${isMine ? 'bg-indigo-50/30 border-indigo-100 text-slate-800 rounded-tl-none' : 'bg-white border-slate-100 text-slate-700 rounded-tl-none'}`}>
                            {msg.text}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* OVERHAULED Input Area: Fixed overlap by using Flex-Row Layout */}
              <div className="p-4 md:p-6 bg-white border-t border-slate-100 flex-shrink-0">
                <div className="max-w-4xl mx-auto w-full">
                  <form onSubmit={handleSendMessage} className="flex items-end gap-3">
                    <div className="flex-1">
                      <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage(e as any);
                          }
                        }}
                        placeholder={`Message ${selectedVillage ? selectedVillage.name : `@${otherParticipant?.nickname || 'Unknown'}`}...`}
                        disabled={isSending}
                        rows={1}
                        className="w-full px-5 py-3 md:px-6 bg-slate-50 border border-slate-200 rounded-[1.5rem] focus:ring-4 focus:ring-indigo-100 focus:border-indigo-400 focus:bg-white text-sm placeholder-slate-400 transition-all resize-none min-h-[48px] max-h-[150px] shadow-inner disabled:opacity-50 overflow-y-auto"
                      />
                    </div>
                    <div className="flex-shrink-0 mb-1">
                      <button
                        type="submit"
                        disabled={!newMessage.trim() || isSending}
                        className="w-12 h-12 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-lg shadow-indigo-500/30 hover:bg-indigo-700 disabled:opacity-30 disabled:shadow-none transition-all active:scale-90"
                      >
                        {isSending ? (
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        ) : (
                          <span className="text-xl translate-x-0.5">➤</span>
                        )}
                      </button>
                    </div>
                  </form>
                  <p className="text-[9px] text-slate-300 mt-2 px-6 hidden sm:block">Press Enter to send message</p>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-12 text-center">
              <div className="w-24 h-24 bg-slate-50 border border-slate-100 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 shadow-sm">
                <span className="text-4xl grayscale opacity-30">💬</span>
              </div>
              <h3 className="font-heading font-bold text-xl text-slate-800">Your Conversations</h3>
              <p className="text-sm text-slate-500 max-w-xs mx-auto mt-2 leading-relaxed">Select a member from the sidebar to continue your discussion in a private, focused view.</p>
            </div>
          )}
        </main>
      </div>

      {/* CREATE VILLAGE MODAL */}
      {isCreateVillageOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-fade-in">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-white shadow-2xl p-6">
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">Form a Local Hub</h2>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">Create a secure community space for you and your trusted connections.</p>
            
            <div className="space-y-5">
               <div>
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2 block">Hub Name</label>
                  <Input label="" value={newVillageName} onChange={e => setNewVillageName(e.target.value)} placeholder="e.g. Bristol Dads Group..." className="mb-0" />
               </div>
               <div>
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2 block">Mission / Focus</label>
                  <Input label="" value={newVillageDesc} onChange={e => setNewVillageDesc(e.target.value)} placeholder="e.g. Weekend meetups..." className="mb-0" />
               </div>
               <div>
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2 block">Hub Type</label>
                  <div className="grid grid-cols-3 gap-2">
                     {[
                        { icon: '🛡️', label: 'Mental Health' },
                        { icon: '⛺', label: 'Outdoors' },
                        { icon: '☕', label: 'Social' },
                        { icon: '💪', label: 'Activities & Exercise' },
                        { icon: '⚽', label: 'Sports' }
                     ].map(type => (
                        <button 
                            key={type.icon} 
                            type="button"
                            onClick={() => setNewVillageShield(type.icon)} 
                            className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl transition-all border ${newVillageShield === type.icon ? 'bg-indigo-50 border-indigo-400 shadow-sm' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'}`}
                        >
                           <span className={`text-2xl ${newVillageShield === type.icon ? '' : 'grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all'}`}>{type.icon}</span>
                           <span className={`text-[9px] font-black uppercase tracking-wider text-center leading-tight ${newVillageShield === type.icon ? 'text-indigo-700' : 'text-slate-500'}`}>{type.label}</span>
                        </button>
                     ))}
                  </div>
               </div>

               <div className="pt-2">
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2 block border-t pt-5">Privacy Setting</label>
                  <div className="flex bg-slate-50 rounded-xl border border-slate-200 p-1">
                     <button type="button" onClick={() => setNewVillageIsPublic(false)} className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${!newVillageIsPublic ? 'bg-white shadow-sm text-indigo-700 border border-slate-200' : 'text-slate-500 hover:text-slate-700'}`}>Private Hub</button>
                     <button type="button" onClick={() => setNewVillageIsPublic(true)} className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${newVillageIsPublic ? 'bg-white shadow-sm text-indigo-700 border border-slate-200' : 'text-slate-500 hover:text-slate-700'}`}>Public Hub</button>
                  </div>
                  {newVillageIsPublic && <p className="text-[10px] font-bold text-amber-600 mt-2 flex items-center gap-1"><span>⚠️</span> Anyone can discover and join</p>}
               </div>

               {newVillageIsPublic && (
                 <div className="relative">
                    <label className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2 block">General Location</label>
                    <Input 
                       label="" 
                       value={newVillageLocation} 
                       onChange={e => {
                           setNewVillageLocation(e.target.value);
                           setSelectedCoordinates(null);
                       }} 
                       placeholder="e.g. Preston, Lancashire" 
                       className="mb-1" 
                    />
                    {isSearchingLocation && <div className="absolute right-4 top-10 text-[10px] text-slate-400 font-bold animate-pulse">Searching...</div>}
                    {locationSuggestions.length > 0 && !selectedCoordinates && (
                       <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-48 overflow-y-auto custom-scrollbar">
                           {locationSuggestions.map((loc, i) => (
                               <button 
                                   key={i} 
                                   type="button"
                                   onClick={() => {
                                       setNewVillageLocation(loc.display_name);
                                       setSelectedCoordinates([parseFloat(loc.lat), parseFloat(loc.lon)]);
                                       setLocationSuggestions([]);
                                   }}
                                   className="w-full text-left px-4 py-3 text-[11px] font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 border-b border-slate-100 last:border-0"
                               >
                                   {loc.display_name}
                               </button>
                           ))}
                       </div>
                    )}
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest italic mt-1">Important: Must be a general town, area, or public venue postcode. Do NOT use your home postcode!</p>
                 </div>
               )}
               
               <div>
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2 block border-t pt-5">Invite Connections</label>
                  <div className="max-h-40 overflow-y-auto custom-scrollbar space-y-2 pr-2">
                     {activeChats.length === 0 ? (
                        <p className="text-xs text-slate-400 italic">No active connections to invite.</p>
                     ) : (
                        activeChats.map(c => {
                           const partnerId = c.users.find(u => u !== currentUser?.uid);
                           const partner = partnerId ? connectedUsers[partnerId] : null;
                           if (!partnerId || !partner) return null;
                           const isSelected = selectedMembersList.includes(partnerId);
                           return (
                              <button key={c.id} onClick={() => setSelectedMembersList(prev => isSelected ? prev.filter(id => id !== partnerId) : [...prev, partnerId])} className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${isSelected ? 'border-indigo-500 bg-indigo-50 shadow-sm' : 'border-slate-100 bg-slate-50 hover:border-slate-300'}`}>
                                 <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center font-bold text-slate-600 text-xs">{partner.nickname?.charAt(0).toUpperCase()}</div>
                                    <span className="text-sm font-bold text-slate-700">@{partner.nickname}</span>
                                 </div>
                                 <div className={`w-5 h-5 rounded-md border flex items-center justify-center ${isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-300 bg-white'}`}>{isSelected && '✓'}</div>
                              </button>
                           );
                        })
                     )}
                  </div>
               </div>
            </div>

            <div className="mt-8 flex gap-3 pt-6 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsCreateVillageOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleCreateVillage} disabled={!newVillageName || isCreatingVillage} className="flex-1 shadow-md">
                 {isCreatingVillage ? 'Creating...' : 'Form Hub'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </Layout>
  );
};

export default DirectMessages;
